import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from 'recharts';
import MarketLayout from '../components/MarketLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_STYLE = {
  staging:   { bg: '#f3f4f6', color: '#374151' },
  published: { bg: '#eff4ff', color: '#3a6ad9' },
  bidding:   { bg: '#f0fde4', color: '#111111' },
  under_loi: { bg: '#fffde4', color: '#111111' },
  closed:    { bg: '#1f2937', color: '#f9fafb' },
  withdrawn: { bg: '#ffe6e9', color: '#ff082f' },
};

const BID_STATUS_STYLE = {
  submitted:    { bg: '#f3f4f6', color: '#374151', label: 'Submitted' },
  loi_pending:  { bg: '#fffde4', color: '#111111', label: 'LOI Pending' },
  loi_signed:   { bg: '#eff4ff', color: '#3a6ad9', label: 'LOI Signed' },
  dd_open:      { bg: '#f0fde4', color: '#111111', label: 'DD Open' },
  rejected:     { bg: '#ffe6e9', color: '#ff082f', label: 'Rejected' },
  withdrawn:    { bg: '#f3f4f6', color: '#6b7280', label: 'Withdrawn' },
};

function fmtMoney(n) {
  const v = Number(n) || 0;
  return v ? `$${Math.round(v).toLocaleString()}` : '—';
}
function fmtUpb(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return v ? `$${v.toLocaleString()}` : '—';
}
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusPill({ status, large }) {
  const s = STATUS_STYLE[status] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: large ? '6px 14px' : '3px 10px',
      borderRadius: 9999,
      backgroundColor: s.bg,
      color: s.color,
      fontSize: large ? 13 : 11,
      fontWeight: 700,
      textTransform: 'capitalize',
    }}>{(status || '—').replace(/_/g, ' ')}</span>
  );
}
function BidStatusPill({ status }) {
  const s = BID_STATUS_STYLE[status] || { bg: '#f3f4f6', color: '#374151', label: status || '—' };
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 9999, backgroundColor: s.bg, color: s.color, fontSize: 11, fontWeight: 700 }}>
      {s.label}
    </span>
  );
}

function topNFromObj(obj, n = 6) {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj)
    .map(([name, value]) => ({ name, value: Number(value) || 0 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, n);
}

export default function MarketPoolDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get('tab') || 'overview';
  const [tab, setTab] = useState(initialTab);
  const [pool, setPool] = useState(null);
  const [loans, setLoans] = useState([]);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [recalcing, setRecalcing] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [reviewingBid, setReviewingBid] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showAddLoan, setShowAddLoan] = useState(false);

  // Editable fields
  const [edit, setEdit] = useState({ pool_name: '', bid_deadline: '', asking_price_pct: '', reserve_price_pct: '', notes: '' });
  const dirty = useMemo(() => {
    if (!pool) return false;
    return (
      (edit.pool_name || '') !== (pool.pool_name || '') ||
      (edit.bid_deadline || '') !== (pool.bid_deadline ? String(pool.bid_deadline).slice(0, 10) : '') ||
      String(edit.asking_price_pct ?? '') !== String(pool.asking_price_pct ?? '') ||
      String(edit.reserve_price_pct ?? '') !== String(pool.reserve_price_pct ?? '') ||
      (edit.notes || '') !== (pool.notes || '')
    );
  }, [edit, pool]);

  const token = localStorage.getItem('doorly_admin_token');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/market/pools/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPool(res.data.pool);
      setLoans(res.data.loans || []);
      setBids(res.data.bids || []);
      setEdit({
        pool_name:        res.data.pool?.pool_name || '',
        bid_deadline:     res.data.pool?.bid_deadline ? String(res.data.pool.bid_deadline).slice(0, 10) : '',
        asking_price_pct: res.data.pool?.asking_price_pct ?? '',
        reserve_price_pct: res.data.pool?.reserve_price_pct ?? '',
        notes:            res.data.pool?.notes || '',
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load pool.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTab = (t) => { setTab(t); setParams({ tab: t }, { replace: true }); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/market/pools/${id}`,
        {
          poolName:        edit.pool_name,
          bidDeadline:     edit.bid_deadline || null,
          askingPricePct:  edit.asking_price_pct === '' ? null : Number(edit.asking_price_pct),
          reservePricePct: edit.reserve_price_pct === '' ? null : Number(edit.reserve_price_pct),
          notes:           edit.notes || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm(`Publish ${pool.pool_number}? All approved + NDA-signed investors will be notified.`)) return;
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/market/pools/${id}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to publish pool.');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalc = async () => {
    setRecalcing(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/market/pools/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to recalculate.');
    } finally {
      setRecalcing(false);
    }
  };

  const handleRemoveLoan = async (loanId, loanNumber) => {
    if (!window.confirm(`Remove loan ${loanNumber} from this pool?`)) return;
    setRemovingId(loanId);
    try {
      await axios.delete(
        `${API_URL}/api/admin/market/pools/${id}/loans/${loanId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove loan.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleAcceptBid = async (bid) => {
    if (!window.confirm(`Accept this bid (${bid.bid_price_pct}% from ${bid.investor_first_name}) and reject all other bids on this pool?`)) return;
    try {
      await axios.patch(
        `${API_URL}/api/admin/market/bids/${bid.id}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to accept bid.');
    }
  };

  const handleRejectBid = async () => {
    if (!reviewingBid) return;
    try {
      await axios.patch(
        `${API_URL}/api/admin/market/bids/${reviewingBid.id}/reject`,
        { rejectionReason: rejectReason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReviewingBid(null);
      setRejectReason('');
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject bid.');
    }
  };

  if (loading) {
    return (
      <MarketLayout>
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
      </MarketLayout>
    );
  }
  if (error || !pool) {
    return (
      <MarketLayout>
        <div style={{ padding: 32, textAlign: 'center', color: '#ff082f' }}>{error || 'Pool not found.'}</div>
      </MarketLayout>
    );
  }

  return (
    <MarketLayout>
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 18 }}>
          <button onClick={() => navigate('/admin/market/pools')} style={{ background: 'transparent', border: 'none', color: '#3a6ad9', fontSize: 13, cursor: 'pointer', padding: 0 }}>
            ← All pools
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>{pool.pool_name}</h1>
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, color: '#6b7280' }}>{pool.pool_number}</span>
              <StatusPill status={pool.status} large />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {pool.status === 'staging' && (
              <button
                onClick={handlePublish}
                disabled={saving || (pool.loan_count || 0) < 1}
                style={{ ...btnPrimary, opacity: ((pool.loan_count || 0) < 1 ? 0.5 : 1) }}
              >
                {saving ? 'Working…' : 'Publish Pool'}
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid #e5e7eb' }}>
          {[
            { k: 'overview', label: 'Overview' },
            { k: 'loans',    label: `Loans (${loans.length})` },
            { k: 'bids',     label: `Bids (${bids.length})` },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => handleTab(t.k)}
              style={{
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: tab === t.k ? '#3a6ad9' : '#6b7280',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: tab === t.k ? '2px solid #3a6ad9' : '2px solid transparent',
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            {/* Left — editable */}
            <div style={card}>
              <div style={cardTitle}>Pool Details</div>
              <Field label="Pool name">
                <input style={inputStyle} value={edit.pool_name} onChange={(e) => setEdit((p) => ({ ...p, pool_name: e.target.value }))} />
              </Field>
              <Field label="Bid deadline">
                <input type="date" style={inputStyle} value={edit.bid_deadline} onChange={(e) => setEdit((p) => ({ ...p, bid_deadline: e.target.value }))} />
              </Field>
              <Field label="Asking price (% of UPB)">
                <input type="number" step="0.01" style={inputStyle} value={edit.asking_price_pct} onChange={(e) => setEdit((p) => ({ ...p, asking_price_pct: e.target.value }))} />
              </Field>
              <Field label="Reserve (Internal — not shown to investors)">
                <input type="number" step="0.01" style={inputStyle} value={edit.reserve_price_pct} onChange={(e) => setEdit((p) => ({ ...p, reserve_price_pct: e.target.value }))} />
              </Field>
              <Field label="Notes">
                <textarea rows={4} style={{ ...inputStyle, height: 'auto', padding: 10, resize: 'vertical' }} value={edit.notes} onChange={(e) => setEdit((p) => ({ ...p, notes: e.target.value }))} />
              </Field>
              <button onClick={handleSave} disabled={!dirty || saving} style={{ ...btnPrimary, opacity: (!dirty || saving) ? 0.5 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>

            {/* Right — summary + charts */}
            <div style={card}>
              <div style={cardTitle}>Summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 18 }}>
                <Stat label="Total UPB" value={fmtUpb(pool.total_upb)} />
                <Stat label="Loan Count" value={pool.loan_count || 0} />
                <Stat label="WA FICO" value={pool.wa_fico || '—'} />
                <Stat label="WA Note Rate" value={pool.wa_note_rate ? `${Number(pool.wa_note_rate).toFixed(2)}%` : '—'} />
                <Stat label="WA LTV" value={pool.wa_original_ltv ? `${Number(pool.wa_original_ltv).toFixed(0)}%` : '—'} />
                <Stat label="Avg. Age" value={pool.wa_loan_age_months ? `${pool.wa_loan_age_months} mo` : '—'} />
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  Geographic distribution (top 5)
                </div>
                <MiniChart data={topNFromObj(pool.geographic_summary, 5)} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  Property type
                </div>
                <MiniChart data={topNFromObj(pool.property_type_summary, 6)} />
              </div>
            </div>
          </div>
        )}

        {tab === 'loans' && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={cardTitle}>Loans in pool</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowAddLoan(true)} style={btnPrimary}>+ Add Loan</button>
                <button onClick={handleRecalc} disabled={recalcing} style={btnGhost}>
                  {recalcing ? 'Recalculating…' : 'Recalculate Summary'}
                </button>
              </div>
            </div>
            {loans.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                No loans in this pool yet.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <Th>Loan #</Th>
                    <Th>State</Th>
                    <Th>Type</Th>
                    <Th align="right">UPB</Th>
                    <Th align="right">Rate</Th>
                    <Th align="right">FICO</Th>
                    <Th align="right">LTV</Th>
                    <Th>Doc Type</Th>
                    <Th align="right">Age</Th>
                    <Th>Added</Th>
                    {pool.status === 'staging' && <Th align="right">Actions</Th>}
                  </tr>
                </thead>
                <tbody>
                  {loans.map((l) => (
                    <tr key={l.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <Td>{l.loan_number}</Td>
                      <Td>{l.property_state || '—'}</Td>
                      <Td>{l.property_type || '—'}</Td>
                      <Td align="right">{fmtMoney(l.current_upb)}</Td>
                      <Td align="right">{l.note_rate ? `${Number(l.note_rate).toFixed(2)}%` : '—'}</Td>
                      <Td align="right">{l.original_fico || '—'}</Td>
                      <Td align="right">{l.original_ltv ? `${l.original_ltv}%` : '—'}</Td>
                      <Td>{l.doc_type || '—'}</Td>
                      <Td align="right">{l.loan_age_months ?? '—'}</Td>
                      <Td>{fmtDate(l.added_to_pool_at)}</Td>
                      {pool.status === 'staging' && (
                        <Td align="right">
                          <button
                            disabled={removingId === l.id}
                            onClick={() => handleRemoveLoan(l.id, l.loan_number)}
                            style={btnRedSmall}
                          >
                            Remove
                          </button>
                        </Td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'bids' && (
          <div style={card}>
            <div style={cardTitle}>Bids ({bids.length})</div>
            {bids.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                No bids on this pool yet.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <Th>Investor</Th>
                    <Th>Firm</Th>
                    <Th align="right">Bid %</Th>
                    <Th align="right">Bid Amount</Th>
                    <Th>Status</Th>
                    <Th>Submitted</Th>
                    <Th align="right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map((b) => (
                    <tr key={b.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <Td>
                        <div style={{ fontWeight: 600 }}>{b.investor_first_name} {b.investor_last_name}</div>
                        <div style={{ color: '#6b7280', fontSize: 12 }}>{b.investor_email}</div>
                      </Td>
                      <Td>{b.investor_firm_name || '—'}</Td>
                      <Td align="right">{Number(b.bid_price_pct).toFixed(2)}%</Td>
                      <Td align="right">{fmtMoney(b.bid_amount)}</Td>
                      <Td><BidStatusPill status={b.status} /></Td>
                      <Td>{fmtDate(b.submitted_at)}</Td>
                      <Td align="right">
                        <BidActionCell
                          bid={b}
                          token={token}
                          onAccept={() => handleAcceptBid(b)}
                          onReject={() => { setReviewingBid(b); setRejectReason(''); }}
                          onChange={load}
                        />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {showAddLoan && <AddLoanModal poolId={id} onClose={() => setShowAddLoan(false)} onAdded={() => { setShowAddLoan(false); load(); }} />}
      {reviewingBid && (
        <div style={modalBackdrop}>
          <div style={modalBox}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
              Reject bid from {reviewingBid.investor_first_name} {reviewingBid.investor_last_name}?
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              Investor will receive an email. Reason is optional.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Optional reason"
              style={{ width: '100%', minHeight: 80, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', marginBottom: 14, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => { setReviewingBid(null); setRejectReason(''); }} style={btnGhost}>Cancel</button>
              <button onClick={handleRejectBid} style={btnRedSmall}>Confirm Reject</button>
            </div>
          </div>
        </div>
      )}
    </MarketLayout>
  );
}

function AddLoanModal({ poolId, onClose, onAdded }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);
  const token = localStorage.getItem('doorly_admin_token');

  const doSearch = async () => {
    if (!search.trim()) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/borrowers`, {
        params: { search: search.trim(), limit: 20, status: 'closed' },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResults(res.data.borrowers || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  const handleAdd = async (userId) => {
    setAdding(userId);
    try {
      await axios.post(
        `${API_URL}/api/admin/market/pools/${poolId}/loans`,
        { userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onAdded();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add loan.');
    } finally {
      setAdding(null);
    }
  };

  return (
    <div style={modalBackdrop}>
      <div style={{ ...modalBox, maxWidth: 640 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 12 }}>Add Loan to Pool</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input
            type="text"
            placeholder="Search by loan number, name, or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }}
            style={{ flex: 1, height: 40, padding: '0 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box' }}
          />
          <button onClick={doSearch} disabled={searching} style={btnPrimary}>
            {searching ? 'Searching…' : 'Search'}
          </button>
        </div>
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {results.length === 0 ? (
            <div style={{ padding: 14, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              {searching ? '' : 'Search closed borrowers above.'}
            </div>
          ) : results.map((u) => (
            <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                  {u.first_name} {u.last_name}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {u.loan_number || 'no loan #'} · {u.email}
                </div>
              </div>
              <button
                onClick={() => handleAdd(u.id)}
                disabled={adding === u.id || !u.loan_number}
                style={{ ...btnPrimary, opacity: (!u.loan_number || adding === u.id) ? 0.5 : 1 }}
              >
                {adding === u.id ? 'Adding…' : 'Add'}
              </button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} style={btnGhost}>Close</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div style={{ backgroundColor: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginTop: 2 }}>{value}</div>
    </div>
  );
}
function MiniChart({ data }) {
  if (!data || data.length === 0) {
    return <div style={{ fontSize: 12, color: '#9ca3af' }}>—</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={Math.max(140, data.length * 28)}>
      <BarChart layout="vertical" data={data} margin={{ left: 12, right: 16, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
        <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} width={70} />
        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
        <Bar dataKey="value" fill="#3a6ad9" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// Per-bid action cell — surfaces accept/reject for new bids, LOI signing
// progress while pending, manual-doc-id recovery if BoldSign auto-create
// failed, completion timestamp + Open DD Room when fully signed, and
// days-remaining once DD is open.
function BidActionCell({ bid, token, onAccept, onReject, onChange }) {
  const [manualId, setManualId] = useState('');
  const [savingManual, setSavingManual] = useState(false);
  const [openingDd, setOpeningDd] = useState(false);

  if (bid.status === 'submitted') {
    return (
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={onAccept} style={btnGreen}>Accept</button>
        <button onClick={onReject} style={btnRedSmall}>Reject</button>
      </div>
    );
  }

  if (bid.status === 'loi_pending') {
    if (bid.loi_document_id) {
      // LOI sent — show per-signer status.
      return (
        <div style={{ textAlign: 'left', minWidth: 220 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#111111', marginBottom: 4 }}>
            LOI sent — awaiting signatures
          </div>
          <div style={{ fontSize: 11, color: '#374151', lineHeight: 1.6 }}>
            Investor: {bid.loi_investor_signed_at ? `✓ Signed ${fmtDate(bid.loi_investor_signed_at)}` : '⏳ Pending'}
          </div>
          <div style={{ fontSize: 11, color: '#374151', lineHeight: 1.6 }}>
            Doorly: {bid.loi_doorly_signed_at ? `✓ Signed ${fmtDate(bid.loi_doorly_signed_at)}` : '⏳ Pending'}
          </div>
        </div>
      );
    }
    // LOI auto-creation failed — manual recovery input.
    return (
      <div style={{ textAlign: 'left', minWidth: 240 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#ff082f', marginBottom: 4 }}>
          ⚠️ LOI creation failed
        </div>
        <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 6px', lineHeight: 1.5 }}>
          Create the LOI manually in BoldSign and paste the document ID below.
        </p>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            value={manualId}
            onChange={(e) => setManualId(e.target.value)}
            placeholder="BoldSign Document ID"
            style={{ flex: 1, height: 30, padding: '0 10px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 11, boxSizing: 'border-box' }}
          />
          <button
            disabled={savingManual || !manualId.trim()}
            onClick={async () => {
              setSavingManual(true);
              try {
                await axios.patch(
                  `${API_URL}/api/admin/market/bids/${bid.id}`,
                  { loiDocumentId: manualId.trim() },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                setManualId('');
                onChange();
              } catch (err) {
                alert(err.response?.data?.error || 'Save failed.');
              } finally {
                setSavingManual(false);
              }
            }}
            style={{ ...btnPrimary, padding: '6px 10px', fontSize: 11, opacity: savingManual || !manualId.trim() ? 0.5 : 1 }}
          >
            {savingManual ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  if (bid.status === 'loi_signed') {
    return (
      <div style={{ textAlign: 'right', minWidth: 220 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#acf44e', marginBottom: 6 }}>
          ✅ LOI Fully Executed
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>
          {fmtDate(bid.loi_completed_at)}
        </div>
        <button
          disabled={openingDd}
          onClick={async () => {
            setOpeningDd(true);
            try {
              await axios.patch(`${API_URL}/api/admin/market/bids/${bid.id}/open-dd`, {}, {
                headers: { Authorization: `Bearer ${token}` },
              });
              onChange();
            } catch (err) {
              alert(err.response?.data?.error || 'Failed.');
            } finally {
              setOpeningDd(false);
            }
          }}
          style={{ ...btnGreen, opacity: openingDd ? 0.6 : 1 }}
        >
          {openingDd ? 'Opening…' : 'Open DD Room'}
        </button>
      </div>
    );
  }

  if (bid.status === 'dd_open') {
    const daysOpen = bid.dd_room_opened_at
      ? Math.floor((Date.now() - new Date(bid.dd_room_opened_at).getTime()) / 86_400_000)
      : 0;
    const daysRemaining = Math.max(0, 30 - daysOpen);
    return (
      <div style={{ textAlign: 'right', minWidth: 200 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#acf44e', marginBottom: 4 }}>
          ✅ DD Room Open
        </div>
        <div style={{ fontSize: 11, color: '#6b7280' }}>
          opened {fmtDate(bid.dd_room_opened_at)}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: daysRemaining < 7 ? '#ff082f' : '#374151', marginTop: 4 }}>
          {daysRemaining} day{daysRemaining === 1 ? '' : 's'} remaining
        </div>
      </div>
    );
  }

  return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
}

const Th = ({ children, align = 'left' }) => (
  <th style={{ textAlign: align, padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{children}</th>
);
const Td = ({ children, align = 'left' }) => (
  <td style={{ padding: '12px 16px', textAlign: align, color: '#111827', verticalAlign: 'middle' }}>{children}</td>
);

const card      = { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 };
const cardTitle = { fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 14 };

const inputStyle = { width: '100%', height: 40, padding: '0 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box' };

const btnBase    = { fontSize: 12, fontWeight: 700, padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer' };
const btnPrimary = { ...btnBase, backgroundColor: '#3a6ad9', color: '#fff' };
const btnGreen   = { ...btnBase, backgroundColor: '#acf44e', color: '#fff' };
const btnRedSmall = { ...btnBase, backgroundColor: '#ff082f', color: '#fff', padding: '6px 12px' };
const btnGhost   = { ...btnBase, backgroundColor: 'transparent', color: '#374151', border: '1px solid #e5e7eb' };

const modalBackdrop = { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const modalBox = { backgroundColor: '#fff', borderRadius: 14, padding: 24, maxWidth: 480, width: '100%' };
